import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import styled from 'styled-components';
import { uploadImages } from '../../services/postApi';

export interface AttachedFile {
  id: string;
  name: string;
  url?: string;
  file?: File;
}

export interface RichTextEditorHandle {
  toggleCodeBlock: () => void;
  focus: () => void;
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  onImageUpload?: (files: FileList) => Promise<string[]>;
  attachedFiles?: AttachedFile[];
  onAttachedFilesChange?: (files: AttachedFile[]) => void;
  replyIdForAttachmentDeletion?: number;
  onDeleteAttachments?: (urls: string[]) => Promise<void>;
  attachmentsEditable?: boolean; // 수정 모드에서만 삭제 버튼 노출
  onPendingFilesChange?: (files: File[]) => void;
  onDeletedAttachmentUrlsChange?: (urls: string[]) => void;
  hideAttachmentButton?: boolean; // 댓글에서는 첨부 버튼 숨김
  hideToolbar?: boolean; // 전체 툴바 숨김
  hideBorder?: boolean; // 보더 숨김
}

interface ToolbarState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  code: boolean;
  fontSize: string;
}

const FONT_SIZE_OPTIONS = [
  { label: '14', value: '14px' },
  { label: '16', value: '16px' },
  { label: '18', value: '18px' },
  { label: '20', value: '20px' },
  { label: '24', value: '24px' },
  { label: '28', value: '28px' },
  { label: '32', value: '32px' },
];

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(({ 
  value,
  onChange,
  placeholder = '내용을 입력해주세요.',
  minHeight = '300px',
  maxHeight = '500px',
  onImageUpload,
  attachedFiles = [],
  onAttachedFilesChange,
  replyIdForAttachmentDeletion,
  onDeleteAttachments,
  attachmentsEditable = false,
  onPendingFilesChange,
  onDeletedAttachmentUrlsChange,
  hideAttachmentButton = true,
  hideToolbar = false,
  hideBorder = false,
}, ref) => {
  const [toolbarState, setToolbarState] = useState<ToolbarState>({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    code: false,
    fontSize: '16px',
  });
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [imageWidthPercent, setImageWidthPercent] = useState<number>(100);
  const [resizePanelPos, setResizePanelPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  
  const editorRef = useRef<HTMLDivElement>(null);
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const pendingFilesRef = useRef<Map<string, File>>(new Map());
  const deletedUrlsRef = useRef<Set<string>>(new Set());
  // 파일 선택 다이얼로그가 열리면 에디터 셀렉션이 사라지므로, 버튼 클릭 시점의 커서 위치를 저장
  const savedSelectionRef = useRef<Range | null>(null);
  const toggleCodeForSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    // 선택 없으면 동작하지 않음
    if (range.collapsed) return;

    // pre 안이면 언랩
    const containerEl = (range.commonAncestorContainer as Element).nodeType === 1
      ? (range.commonAncestorContainer as Element)
      : (range.commonAncestorContainer as Node).parentElement as Element | null;
    const preAncestor = containerEl?.closest('pre');
    if (preAncestor) {
      const textContent = preAncestor.textContent || '';
      const textNode = document.createTextNode(textContent);
      preAncestor.replaceWith(textNode);
      const newRange = document.createRange();
      newRange.setStart(textNode, 0);
      newRange.setEnd(textNode, textNode.length);
      selection.removeAllRanges();
      selection.addRange(newRange);
      onChange(editorRef.current?.innerHTML || '');
      return;
    }

    // 적용: 선택 영역을 <pre><code>로 감싸기
    const pre = document.createElement('pre');
    const code = document.createElement('code');
    pre.appendChild(code);
    const frag = range.cloneContents();
    code.appendChild(frag);
    range.deleteContents();
    range.insertNode(pre);
    const newRange = document.createRange();
    newRange.selectNodeContents(code);
    selection.removeAllRanges();
    selection.addRange(newRange);
    onChange(editorRef.current?.innerHTML || '');
  };

  useImperativeHandle(ref, () => ({
    toggleCodeBlock: toggleCodeForSelection,
    focus: () => editorRef.current?.focus(),
  }));

  const executeCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    updateToolbarState();
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const applyFontSize = (fontSize: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      setToolbarState((prev) => ({ ...prev, fontSize }));
      editorRef.current?.focus();
      return;
    }

    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('fontSize', false, '7');

    if (editorRef.current) {
      const fontNodes = Array.from(editorRef.current.querySelectorAll('font[size="7"]'));
      fontNodes.forEach((node) => {
        const span = document.createElement('span');
        span.style.fontSize = fontSize;
        span.innerHTML = node.innerHTML;
        node.replaceWith(span);
      });
      onChange(editorRef.current.innerHTML);
    }

    updateToolbarState();
  };

  const updateToolbarState = () => {
    try {
      // Modern approach: 선택 영역이 있을 때만 상태 체크
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        // 선택 영역이 없으면 모든 상태를 false로 설정
        setToolbarState({
          bold: false,
          italic: false,
          underline: false,
          strikethrough: false,
          code: false,
          fontSize: '16px',
        });
        return;
      }

      // deprecated warning 억제를 위한 타입 캐스팅과 try-catch
      const queryState = (command: string): boolean => {
        try {
          // @ts-ignore - deprecated API이지만 대체 API가 완전히 준비되지 않음
          return document.queryCommandState(command);
        } catch (error) {
          /// console.warn(`queryCommandState for ${command} failed:`, error);
          return false;
        }
      };

      setToolbarState({
        bold: queryState('bold'),
        italic: queryState('italic'),
        underline: queryState('underline'),
        strikethrough: queryState('strikeThrough'),
        code: queryState('code'),
        fontSize: selection.anchorNode?.parentElement
          ? window.getComputedStyle(selection.anchorNode.parentElement).fontSize || '16px'
          : '16px',
      });
    } catch (error) {
      /// console.warn('Failed to update toolbar state:', error);
      // 에러 발생 시 기본 상태로 설정
      setToolbarState({
        bold: false,
        italic: false,
        underline: false,
        strikethrough: false,
        code: false,
        fontSize: '16px',
      });
    }
  };

  const handleAttachmentClick = () => {
    attachmentInputRef.current?.click();
  };

  const handleAttachmentSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setIsFileUploading(true);
      try {
        // 저장 시점까지 서버 업로드하지 않고 Blob URL로만 미리보기

        // 에디터 하단에 첨부파일 HTML 블록 추가/갱신
        const editor = editorRef.current;
        if (editor) {
          // 기존 첨부 블록 탐색
          let list = editor.querySelector('ul.tbc-attachments') as HTMLUListElement | null;

          // 없으면 생성
          if (!list) {
            list = document.createElement('ul');
            list.setAttribute('class', 'tbc-attachments');
            list.setAttribute('contenteditable', 'false');
            editor.appendChild(list);
          }
          // 추가되면 다시 표시
          list.style.display = '';

          // 파일명 안전 처리
          const escapeHtml = (unsafe: string) => unsafe
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

          // 각 파일을 li로 추가 (Blob URL 미리보기)
          Array.from(files).forEach((file) => {
            const blobUrl = URL.createObjectURL(file);
            // const isImage = file.type.startsWith('image/');
            const fileName = escapeHtml(file.name);

            // 첨부는 저장 시 태그만 남기므로 썸네일 HTML은 저장하지 않음

            const deleteBtnHtml = attachmentsEditable
              ? `<a href="#" role="button" tabindex="0" class="tbc-attachment__delete" data-pending="true" data-blob="${blobUrl}" aria-label="첨부파일 삭제" title="첨부파일 삭제"></a>`
              : '';
            // a 안에 span 2개: 첫번째 파일명, 두번째 원본 URL(이미지/파일 공통)
            const itemHtml = `<li><a href="${blobUrl}" target="_blank" rel="noopener"><span>${fileName}</span><span>${blobUrl}</span></a>${deleteBtnHtml}</li>`;

            list!.insertAdjacentHTML('beforeend', itemHtml);
            pendingFilesRef.current.set(blobUrl, file);
          });

          // 항상 맨 하단에 위치하도록 이동
          editor.appendChild(list);

          // 변경사항 반영 및 상위로 pending 파일 목록 전달
          onChange(editor.innerHTML);
          if (onPendingFilesChange) {
            const uniqueFiles = Array.from(new Map(Array.from(pendingFilesRef.current.values()).map(f => [`${f.name}_${f.size}_${f.lastModified}`, f])).values());
            onPendingFilesChange(uniqueFiles);
          }
        }
      } catch (error) {
        /// console.error('파일 업로드 실패:', error);
        let errorMessage = '파일 업로드에 실패했습니다.';
        
        if (error instanceof Error) {
          if (error.message.includes('Failed to fetch')) {
            errorMessage = '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.';
          } else if (error.message.includes('401')) {
            errorMessage = '로그인이 필요합니다. 다시 로그인해주세요.';
          } else if (error.message.includes('413')) {
            errorMessage = '파일 크기가 너무 큽니다. 더 작은 파일을 선택해주세요.';
          } else {
            errorMessage = error.message;
          }
        }
        
        alert(errorMessage);
      } finally {
        setIsFileUploading(false);
      }
    }
    // 파일 선택 초기화
    event.target.value = '';
  };

  // 첨부파일은 에디터 HTML 내에서 관리하므로 별도 제거 UI는 제공하지 않음

  const handleImageClick = () => {
    // 현재 커서가 에디터 안에 있으면 위치 저장 (다이얼로그가 닫히면 셀렉션이 유실됨)
    const selection = window.getSelection();
    if (
      selection &&
      selection.rangeCount > 0 &&
      editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer)
    ) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    } else {
      savedSelectionRef.current = null;
    }
    fileInputRef.current?.click();
  };

  // execCommand('insertHTML')는 셀렉션이 에디터 밖에 있으면 아무 동작도 하지 않으므로
  // 저장해둔 커서 위치(없으면 본문 끝)에 DOM으로 직접 삽입
  const insertImagesIntoEditor = (urls: string[]) => {
    const editor = editorRef.current;
    if (!editor || urls.length === 0) return;

    editor.focus();

    let range: Range;
    const saved = savedSelectionRef.current;
    if (saved && editor.contains(saved.commonAncestorContainer)) {
      range = saved;
      range.collapse(false);
    } else {
      range = document.createRange();
      const attachmentsList = editor.querySelector('ul.tbc-attachments');
      if (attachmentsList) {
        range.setStartBefore(attachmentsList);
      } else {
        range.selectNodeContents(editor);
      }
      range.collapse(false);
    }

    urls.forEach((url) => {
      const img = document.createElement('img');
      img.src = url;
      img.alt = '업로드된 이미지';
      img.setAttribute('style', 'display: block; max-width: 100%; height: auto; margin: 10px 0;');
      range.insertNode(img);
      range.setStartAfter(img);
      range.collapse(true);
    });

    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    savedSelectionRef.current = null;

    onChange(editor.innerHTML);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setIsImageUploading(true);
      try {
        // 서버에 이미지 업로드
        const response = await uploadImages(files);
        const imageUrls = response.data;

        // 업로드된 이미지 URL들을 에디터에 삽입
        insertImagesIntoEditor(imageUrls || []);
      } catch (error) {
        /// console.error('이미지 업로드 실패:', error);
        let errorMessage = '이미지 업로드에 실패했습니다.';
        
        if (error instanceof Error) {
          if (error.message.includes('Failed to fetch')) {
            errorMessage = '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.';
          } else if (error.message.includes('401')) {
            errorMessage = '로그인이 필요합니다. 다시 로그인해주세요.';
          } else if (error.message.includes('413')) {
            errorMessage = '파일 크기가 너무 큽니다. 더 작은 이미지를 선택해주세요.';
          } else {
            errorMessage = error.message;
          }
        }
        
        alert(errorMessage);
      } finally {
        setIsImageUploading(false);
      }
    }
    // 파일 선택 초기화
    event.target.value = '';
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      const editor = editorRef.current;
      // 첨부 블록이 항상 맨 아래로 오도록 정리
      const list = editor.querySelector('ul.tbc-attachments') as HTMLUListElement | null;
      if (list && list !== editor.lastElementChild) {
        editor.appendChild(list);
      }
      if (list) {
        list.setAttribute('contenteditable', 'false');
        // 첨부 블록 내부에 개행 <br>가 섞여 들어오는 경우 정리
        list.querySelectorAll('br').forEach((br) => br.parentNode?.removeChild(br));
        list.querySelectorAll('li.tbc-attachment').forEach((li) => {
          li.querySelectorAll('br').forEach((br) => br.parentNode?.removeChild(br));
        });
      }
      const content = editor.innerHTML;
      onChange(content);
    }
    updateToolbarState();
  };

  const handleEditorFocus = () => {
    updateToolbarState();
  };

  const handleKeyUp = () => {
    updateToolbarState();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const pre = (range.startContainer as Element).parentElement?.closest('pre');
      if (pre) {
        e.preventDefault();
        if (e.shiftKey) {
          // outdent: 커서 앞의 두 칸 공백 제거
          if (range.startContainer.nodeType === Node.TEXT_NODE) {
            const tn = range.startContainer as Text;
            const off = range.startOffset;
            const val = tn.data;
            if (off >= 2 && val.slice(off - 2, off) === '  ') {
              tn.deleteData(off - 2, 2);
              const newRange = document.createRange();
              newRange.setStart(tn, off - 2);
              newRange.collapse(true);
              sel.removeAllRanges();
              sel.addRange(newRange);
            }
          }
        } else {
          // indent: 두 칸 공백 삽입
          try {
            // @ts-ignore deprecated but works for contenteditable
            document.execCommand('insertText', false, '  ');
          } catch {
            const space = document.createTextNode('  ');
            range.insertNode(space);
            const newRange = document.createRange();
            newRange.setStartAfter(space);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
          }
        }
        onChange(editorRef.current?.innerHTML || '');
      }
    }
  
    // Enter 키 처리 추가
    if (e.key === 'Enter' && !e.shiftKey) {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const pre = (range.startContainer as Element).parentElement?.closest('pre');
      if (pre) {
        e.preventDefault();
        
        // 코드블록 다음에 일반 paragraph 생성
        const p = document.createElement('p');
        p.innerHTML = '<br>'; // 빈 paragraph에 br 추가하여 커서 위치 확보
        
        // 코드블록 바로 다음에 paragraph 삽입
        pre.parentNode?.insertBefore(p, pre.nextSibling);
        
        // 커서를 새 paragraph로 이동
        const newRange = document.createRange();
        newRange.setStart(p, 0);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        
        // 변경사항 반영
        onChange(editorRef.current?.innerHTML || '');
      }
    }
  };

  const updateResizePanelPosition = useCallback(() => {
    if (!selectedImage || !editorWrapperRef.current) return;
    const imgRect = selectedImage.getBoundingClientRect();
    const wrapRect = editorWrapperRef.current.getBoundingClientRect();
    setResizePanelPos({
      top: Math.max(0, imgRect.top - wrapRect.top) + 4,
      left: Math.max(0, imgRect.right - wrapRect.left) - 180,
    });
  }, [selectedImage]);

  useEffect(() => {
    if (!selectedImage) return;
    updateResizePanelPosition();
    const onWin = () => updateResizePanelPosition();
    window.addEventListener('scroll', onWin, true);
    window.addEventListener('resize', onWin);
    return () => {
      window.removeEventListener('scroll', onWin, true);
      window.removeEventListener('resize', onWin);
    };
  }, [selectedImage, updateResizePanelPosition]);

  useEffect(() => {
    // 브라우저 오브젝트 리사이징 활성화 (가능한 경우)
    try {
      // @ts-ignore
      document.execCommand('enableObjectResizing', false, true);
    } catch {}
  }, []);

  // useEffect로 외부 value 변경사항을 에디터에 반영
  useEffect(() => {
    if (!editorRef.current) return;
    // 값이 달라졌을 때만 innerHTML을 갱신해 커서 점프를 막는다
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
    // 항상(값이 동일해도) 편집모드에서는 첨부 블록을 카드 UI로 보강
    if (attachmentsEditable) {
      const editor = editorRef.current;
      const uls = Array.from(editor.querySelectorAll('ul')) as HTMLUListElement[];
      const lastUl = uls[uls.length - 1];
      if (lastUl) {
        lastUl.setAttribute('contenteditable', 'false');
        lastUl.classList.add('tbc-attachments');
        // 불필요한 <br> 제거
        lastUl.querySelectorAll('br').forEach((br) => br.parentNode?.removeChild(br));
        // 내용 없는 li 정리
        Array.from(lastUl.querySelectorAll('li')).forEach((li) => {
          const hasAnchor = !!li.querySelector('a');
          const hasText = (li.textContent || '').trim().length > 0;
          if (!hasAnchor && !hasText) li.remove();
        });
        // li들을 카드 UI로 보강하고 삭제 버튼 추가
        const lis = Array.from(lastUl.querySelectorAll('li')) as HTMLLIElement[];
        lis.forEach((li) => {
          li.classList.add('tbc-attachment');
          const link = li.querySelector('a') as HTMLAnchorElement | null;
          if (!link) return;
          link.classList.add('tbc-attachment__link');
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener');
          // a 내부 span 텍스트들에서 URL/이름을 유연하게 복구
          const spans = Array.from(link.querySelectorAll('span')) as HTMLSpanElement[];
          const texts = spans.map((s) => (s.textContent || '').trim()).filter(Boolean);
          const urlFromSpan = (texts.find((t) => /^(https?:|blob:)/i.test(t)) || link.getAttribute('href') || '').trim();
          const fileUrl = urlFromSpan;
          const candidateName = (texts.find((t) => t !== fileUrl && !/^(https?:|blob:)/i.test(t)) || '').trim();
          const nameFromUrl = (u: string) => {
            try {
              let path = decodeURIComponent(u.split('?')[0] || '');
              path = path.substring(path.lastIndexOf('/') + 1);
              if (!path) return '';
              const lastDot = path.lastIndexOf('.');
              const ext = lastDot > -1 ? path.substring(lastDot) : '';
              const base = lastDot > -1 ? path.substring(0, lastDot) : path;
              if (base.includes('_')) {
                const after = base.split('_').slice(1).join('_');
                return after ? `${after}${ext}` : `${base}${ext}`;
              }
              return `${base}${ext}`;
            } catch {
              return u;
            }
          };
          const fileName = candidateName || nameFromUrl(fileUrl);
          // 링크 내용 비우고 썸네일/아이콘 + 파일명 재구성
          link.textContent = '';
          const thumbWrap = document.createElement('span');
          thumbWrap.className = 'tbc-attachment__thumb-wrap';
          // 모든 첨부파일에 클립 모양 아이콘 사용
          thumbWrap.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.3504 3.9001C9.02491 3.9001 7.95039 4.97461 7.95039 6.3001V16.0501C7.95039 18.2868 9.76364 20.1001 12.0004 20.1001C14.2371 20.1001 16.0504 18.2869 16.0504 16.0501V5.93033C16.0504 5.43327 16.4533 5.03033 16.9504 5.03033C17.4474 5.03033 17.8504 5.43327 17.8504 5.93033V16.0501C17.8504 19.281 15.2313 21.9001 12.0004 21.9001C8.76953 21.9001 6.15039 19.281 6.15039 16.0501V6.3001C6.15039 3.9805 8.0308 2.1001 10.3504 2.1001C12.67 2.1001 14.5504 3.9805 14.5504 6.3001V16.0013C14.5504 17.4096 13.4087 18.5513 12.0004 18.5513C10.5921 18.5513 9.45039 17.4096 9.45039 16.0013V7.18614C9.45039 6.68909 9.85333 6.28614 10.3504 6.28614C10.8474 6.28614 11.2504 6.68909 11.2504 7.18614V16.0013C11.2504 16.4155 11.5862 16.7513 12.0004 16.7513C12.4146 16.7513 12.7504 16.4155 12.7504 16.0013V6.3001C12.7504 4.97461 11.6759 3.9001 10.3504 3.9001Z" fill="currentColor"/></svg>';
          link.appendChild(thumbWrap);
          const nameSpan = document.createElement('span');
          nameSpan.className = 'tbc-attachment__name';
          nameSpan.textContent = fileName || fileUrl;
          link.appendChild(nameSpan);
            // href 보정 및 다운로드 속성 보강
            link.setAttribute('href', fileUrl);
            link.setAttribute('download', fileName || '');
          li.setAttribute('data-url', fileUrl);
          // 삭제 버튼(중복 방지)
          if (!li.querySelector('.tbc-attachment__delete')) {
            const del = document.createElement('a');
            del.setAttribute('href', '#');
            del.setAttribute('role', 'button');
            del.setAttribute('tabindex', '0');
            del.className = 'tbc-attachment__delete';
            del.setAttribute('data-pending', fileUrl.startsWith('blob:') ? 'true' : 'false');
            del.setAttribute('data-blob', fileUrl);
            del.setAttribute('aria-label', '첨부파일 삭제');
            del.setAttribute('title', '첨부파일 삭제');
            li.appendChild(del);
          }
        });
        // 비었으면 숨김, 있으면 표시
        const remaining = lastUl.querySelectorAll('li').length;
        lastUl.style.display = remaining > 0 ? '' : 'none';
      }
    }
  }, [value, attachmentsEditable]);

  return (
    <EditorContainer className="tbc-editor" hideBorder={hideBorder}>
      {!hideToolbar && (
      <Toolbar>
        <ToolbarGroup>
          <ToolbarSelect
            aria-label="글자 크기"
            value={toolbarState.fontSize}
            onChange={(e) => applyFontSize(e.target.value)}
          >
            {FONT_SIZE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </ToolbarSelect>

          <ToolbarButton
            active={toolbarState.bold}
            onClick={() => executeCommand('bold')}
            title="Bold"
            data-tooltip="굵게"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.64999 20.5C7.80992 20.5 7.38988 20.5 7.06901 20.3365C6.78677 20.1927 6.5573 19.9632 6.41349 19.681C6.25 19.3601 6.25 18.9401 6.25 18.1V5.89902C6.25 5.05894 6.25 4.63891 6.41349 4.31804C6.5573 4.03579 6.78677 3.80632 7.06901 3.66251C7.38988 3.49902 7.80992 3.49902 8.64999 3.49902H11.9395C15.1565 3.49902 17.2934 5.26017 17.2934 7.86667C17.2934 9.6513 16.2836 10.8958 14.4755 11.4594V11.5533C16.8355 12.0817 18.1622 13.5258 18.1622 15.6862C18.1622 18.5745 15.814 20.5 12.3152 20.5H8.64999ZM12.3387 18.5745C14.6869 18.5745 16.0488 17.4473 16.0488 15.5453C16.0488 13.6432 14.6164 12.5161 12.1508 12.5161H8.31641V18.5745H12.3387ZM11.7751 10.6375C13.9355 10.6375 15.18 9.67479 15.18 8.03105C15.18 6.38731 14.0059 5.42455 11.963 5.42455H8.31641V10.6375H11.7751Z"
                fill="currentColor"
              />
            </svg>
          </ToolbarButton>

          <ToolbarButton
            active={toolbarState.strikethrough}
            onClick={() => executeCommand('strikeThrough')}
            title="Strikethrough"
            data-tooltip="취소선"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M6.7732 18.4636C8.14077 20.0653 9.81767 20.8289 11.9978 20.8289C15.5201 20.8289 17.8683 18.8094 17.8683 15.7568C17.8683 14.6108 17.5459 13.6749 16.8521 12.9H20.4995C20.9966 12.9 21.3995 12.4971 21.3995 12C21.3995 11.5029 20.9966 11.1 20.4995 11.1H13.8678C13.506 10.9702 13.1178 10.8476 12.7023 10.7316L11.8569 10.4968C9.62615 9.88626 8.85124 9.22876 8.85124 7.93725C8.85124 6.27003 10.1193 5.1429 11.9978 5.1429C13.2411 5.1429 14.3483 5.64157 15.2226 6.59042C15.598 6.9978 16.2022 7.15553 16.6816 6.87798C17.1893 6.58402 17.3583 5.92143 16.9767 5.47576C15.7215 4.00984 13.9436 3.17041 11.9978 3.17041C8.94517 3.17041 6.73787 5.18986 6.73787 7.93725C6.73787 9.26788 7.24987 10.2998 8.34589 11.1H3.49961C3.00255 11.1 2.59961 11.5029 2.59961 12C2.59961 12.4971 3.00255 12.9 3.49961 12.9H13.0043C15.0216 13.5863 15.7549 14.3874 15.7549 15.7568C15.7549 17.6823 14.3225 18.8564 11.9978 18.8564C10.5407 18.8564 9.46932 18.3902 8.47786 17.2849C8.10796 16.8726 7.49734 16.7417 7.02973 17.0387C6.53573 17.3525 6.3932 18.0185 6.7732 18.4636Z"
                fill="currentColor"
              />
            </svg>
          </ToolbarButton>

          <ToolbarButton
            active={toolbarState.underline}
            onClick={() => executeCommand('underline')}
            title="Underline"
            data-tooltip="밑줄"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M11.9979 17.8287C8.09995 17.8287 5.51694 14.9639 5.51694 10.6902V4.03321C5.51694 3.46258 5.97953 3 6.55015 3C7.12077 3 7.58335 3.46258 7.58335 4.03321V10.6902C7.58335 13.7898 9.34449 15.8562 11.9979 15.8562C14.6514 15.8562 16.4125 13.7898 16.4125 10.6902V4.03321C16.4125 3.46258 16.8751 3 17.4457 3C18.0164 3 18.479 3.46258 18.479 4.03321V10.6902C18.479 14.9639 15.8959 17.8287 11.9979 17.8287Z"
                fill="currentColor"
              />
              <path
                d="M4.99961 20.1C4.50255 20.1 4.09961 20.5029 4.09961 21C4.09961 21.4971 4.50255 21.9 4.99961 21.9H18.9995C19.4966 21.9 19.8995 21.4971 19.8995 21C19.8995 20.5029 19.4966 20.1 18.9995 20.1H4.99961Z"
                fill="currentColor"
              />
            </svg>
          </ToolbarButton>
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <ToolbarButton
            onClick={handleImageClick}
            title="이미지 업로드"
            disabled={isImageUploading}
            data-tooltip="이미지"
          >
            {isImageUploading ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="31.416" opacity="0.3">
                  <animate attributeName="strokeDashoffset" dur="2s" values="31.416;0" repeatCount="indefinite" />
                </circle>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.34947 8.74903C6.34947 7.42354 7.42398 6.34903 8.74946 6.34903C10.0749 6.34903 11.1495 7.42354 11.1495 8.74903C11.1495 10.0745 10.0749 11.149 8.74946 11.149C7.42398 11.149 6.34947 10.0745 6.34947 8.74903ZM8.74946 8.14903C8.41809 8.14903 8.14946 8.41766 8.14946 8.74903C8.14946 9.0804 8.41809 9.34903 8.74946 9.34903C9.08083 9.34903 9.34946 9.0804 9.34946 8.74903C9.34946 8.41766 9.08083 8.14903 8.74946 8.14903Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.26189 2.59961C7.45387 2.5996 6.79298 2.59959 6.25591 2.64347C5.7 2.68889 5.19793 2.78578 4.72904 3.02469C3.99521 3.39859 3.39859 3.99522 3.02468 4.72905C2.78577 5.19794 2.68889 5.70002 2.64347 6.25593C2.59959 6.793 2.5996 7.45385 2.59961 8.26189V15.7373C2.5996 16.5454 2.59959 17.2062 2.64347 17.7433C2.68889 18.2992 2.78577 18.8013 3.02468 19.2702C3.39859 20.004 3.99521 20.6006 4.72904 20.9745C5.19793 21.2135 5.7 21.3103 6.25591 21.3558C6.79299 21.3996 7.45384 21.3996 8.26189 21.3996H15.7373C16.5453 21.3996 17.2062 21.3996 17.7432 21.3558C18.2991 21.3103 18.8012 21.2135 19.2701 20.9745C20.0039 20.6006 20.6006 20.004 20.9745 19.2702C21.2134 18.8013 21.3103 18.2992 21.3557 17.7433C21.3996 17.2062 21.3996 16.5454 21.3995 15.7373V8.26191C21.3996 7.45386 21.3996 6.79301 21.3557 6.25593C21.3103 5.70002 21.2134 5.19794 20.9745 4.72905C20.6006 3.99522 20.0039 3.39859 19.2701 3.02469C18.8012 2.78578 18.2991 2.68889 17.7432 2.64347C17.2062 2.59959 16.5454 2.5996 15.7373 2.59961H8.26189ZM5.54622 4.6285C5.71907 4.54043 5.95789 4.47382 6.40249 4.4375C6.8576 4.40031 7.44466 4.39961 8.29959 4.39961H15.6996C16.5545 4.39961 17.1416 4.40031 17.5967 4.4375C18.0413 4.47382 18.2801 4.54043 18.4529 4.6285C18.8481 4.82983 19.1693 5.15109 19.3707 5.54623C19.4587 5.71908 19.5253 5.95791 19.5617 6.40251C19.5989 6.85761 19.5996 7.44468 19.5996 8.29961V10.8275L19.3735 10.6014C18.9989 10.2267 18.6798 9.90768 18.3965 9.66715C18.0988 9.41446 17.7819 9.19599 17.3956 9.07046C16.8131 8.88122 16.1857 8.88122 15.6033 9.07046C15.2169 9.19599 14.9 9.41446 14.6023 9.66715C14.319 9.90767 14 10.2267 13.6254 10.6014L5.13281 19.0939C4.92563 18.9163 4.75382 18.699 4.62849 18.453C4.54042 18.2802 4.47381 18.0413 4.43749 17.5967C4.4003 17.1416 4.3996 16.5545 4.3996 15.6996V8.29961C4.3996 7.44468 4.4003 6.85761 4.43749 6.40251C4.47381 5.95791 4.54042 5.71908 4.62849 5.54623C4.82982 5.15109 5.15108 4.82983 5.54622 4.6285ZM7.17809 19.5943C7.49378 19.5994 7.86152 19.5996 8.29959 19.5996H15.6996C16.5545 19.5996 17.1416 19.5989 17.5967 19.5617C18.0413 19.5254 18.2801 19.4588 18.4529 19.3707C18.8481 19.1694 19.1693 18.8481 19.3707 18.453C19.4587 18.2802 19.5253 18.0413 19.5617 17.5967C19.5989 17.1416 19.5996 16.5545 19.5996 15.6996V13.373L18.1257 11.8992C17.7192 11.4927 17.4521 11.2266 17.2316 11.0394C17.019 10.8589 16.9097 10.8052 16.8393 10.7824C16.6184 10.7106 16.3804 10.7106 16.1595 10.7824C16.0892 10.8052 15.9798 10.8589 15.7672 11.0394C15.5467 11.2266 15.2796 11.4927 14.8731 11.8992L7.17809 19.5943Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </ToolbarButton>

         {hideAttachmentButton && <ToolbarButton
            onClick={handleAttachmentClick}
            title="첨부파일"
            disabled={isFileUploading}
            data-tooltip="파일 첨부"
          >
            {isFileUploading ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="31.416" opacity="0.3">
                  <animate attributeName="strokeDashoffset" dur="2s" values="31.416;0" repeatCount="indefinite" />
                </circle>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M10.3504 3.9001C9.02491 3.9001 7.95039 4.97461 7.95039 6.3001V16.0501C7.95039 18.2868 9.76364 20.1001 12.0004 20.1001C14.2371 20.1001 16.0504 18.2869 16.0504 16.0501V5.93033C16.0504 5.43327 16.4533 5.03033 16.9504 5.03033C17.4474 5.03033 17.8504 5.43327 17.8504 5.93033V16.0501C17.8504 19.281 15.2313 21.9001 12.0004 21.9001C8.76953 21.9001 6.15039 19.281 6.15039 16.0501V6.3001C6.15039 3.9805 8.0308 2.1001 10.3504 2.1001C12.67 2.1001 14.5504 3.9805 14.5504 6.3001V16.0013C14.5504 17.4096 13.4087 18.5513 12.0004 18.5513C10.5921 18.5513 9.45039 17.4096 9.45039 16.0013V7.18614C9.45039 6.68909 9.85333 6.28614 10.3504 6.28614C10.8474 6.28614 11.2504 6.68909 11.2504 7.18614V16.0013C11.2504 16.4155 11.5862 16.7513 12.0004 16.7513C12.4146 16.7513 12.7504 16.4155 12.7504 16.0013V6.3001C12.7504 4.97461 11.6759 3.9001 10.3504 3.9001Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </ToolbarButton>}
        </ToolbarGroup>

        <ToolbarDivider />

        {(
        <ToolbarGroup>
          <ToolbarButton
            onClick={toggleCodeForSelection}
            title="코드 블록"
            data-tooltip="코드 블록"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M14.2187 3.12669C14.7009 3.24725 14.9941 3.73589 14.8736 4.2181L10.8736 20.2181C10.753 20.7003 10.2644 20.9935 9.78217 20.873C9.29996 20.7524 9.00678 20.2638 9.12733 19.7815L13.1273 3.78154C13.2479 3.29932 13.7365 3.00614 14.2187 3.12669Z"
                fill="currentColor"
              />
              <path
                d="M16.364 6.36343C16.0126 6.7149 16.0126 7.28475 16.364 7.63622L20.7276 11.9998L16.364 16.3634C16.0126 16.7149 16.0126 17.2847 16.364 17.6362C16.7155 17.9877 17.2854 17.9877 17.6368 17.6362L22.6368 12.6362C22.9883 12.2847 22.9883 11.7149 22.6368 11.3634L17.6368 6.36343C17.2854 6.01195 16.7155 6.01195 16.364 6.36343Z"
                fill="currentColor"
              />
              <path
                d="M7.63696 7.63622C7.98843 7.28475 7.98843 6.7149 7.63696 6.36343C7.28549 6.01195 6.71564 6.01195 6.36417 6.36343L1.36419 11.3634C1.01272 11.7149 1.01272 12.2847 1.36419 12.6362L6.36417 17.6362C6.71564 17.9877 7.28549 17.9877 7.63696 17.6362C7.98843 17.2847 7.98843 16.7149 7.63696 16.3634L3.27337 11.9998L7.63696 7.63622Z"
                fill="currentColor"
              />
            </svg>
          </ToolbarButton>
        </ToolbarGroup>
        )}
      </Toolbar>
      )}

      <EditorWrapper ref={editorWrapperRef}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.tagName?.toLowerCase() === 'img' && attachmentsEditable) {
            const img = target as HTMLImageElement;
            setSelectedImage(img);
            const widthMatch = /max-width:\s*(\d+)%/.exec(img.getAttribute('style') || '');
            if (widthMatch) {
              setImageWidthPercent(Number(widthMatch[1]));
            } else {
              setImageWidthPercent(100);
            }
          } else if (!(target.closest('.tbc-image-resize-panel'))) {
            setSelectedImage(null);
          }
        }}
      >
        <Editor
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          onBeforeInput={(e) => {
            const native = (e as unknown as { nativeEvent?: InputEvent }).nativeEvent;
            const inputType = native?.inputType || '';
            const selection = window.getSelection();
            const anchorNode = selection?.anchorNode as Node | null;
            const withinAttachments = !!(anchorNode && (anchorNode as Element).parentElement?.closest('ul.tbc-attachments'));
            if (withinAttachments && /delete|cut/i.test(inputType)) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onInput={handleEditorInput}
          onFocus={handleEditorFocus}
          onKeyUp={handleKeyUp}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            // 코드 블록 처리는 툴바 버튼에서만 수행
            const deleteBtn = target.closest('.tbc-attachment__delete') as HTMLElement | null;
            if (!deleteBtn) return;
            if (!attachmentsEditable) return;
            e.preventDefault();
            const editor = editorRef.current;
            const item = deleteBtn.closest('li.tbc-attachment') as HTMLLIElement | null;
            if (!editor || !item) return;
            const isPending = item.getAttribute('data-pending') === 'true';
            if (isPending) {
              const blob = item.getAttribute('data-blob') || deleteBtn.getAttribute('data-blob') || '';
              if (blob) {
                pendingFilesRef.current.delete(blob);
              }
              item.remove();
              onChange(editor.innerHTML);
              if (onPendingFilesChange) {
                const uniqueFiles = Array.from(new Map(Array.from(pendingFilesRef.current.values()).map(f => [`${f.name}_${f.size}_${f.lastModified}`, f])).values());
                onPendingFilesChange(uniqueFiles);
              }
            } else {
              const url = item.getAttribute('data-url') || (item.querySelector('a.tbc-attachment__link') as HTMLAnchorElement | null)?.href || '';
              if (url && onDeletedAttachmentUrlsChange) {
                const next = new Set(deletedUrlsRef.current);
                next.add(url);
                deletedUrlsRef.current = next;
                onDeletedAttachmentUrlsChange(Array.from(next));
              }
              item.remove();
              onChange(editor.innerHTML);
            }
            // li 정리 후, 첨부가 비면 ul 숨김
            const list = editor.querySelector('ul.tbc-attachments') as HTMLUListElement | null;
            if (list) {
              // 내용 없는 li 제거
              Array.from(list.querySelectorAll('li')).forEach((li) => {
                const hasAnchor = !!li.querySelector('a');
                const hasText = (li.textContent || '').trim().length > 0;
                if (!hasAnchor && !hasText) li.remove();
              });
              const remaining = list.querySelectorAll('li').length;
              list.style.display = remaining > 0 ? '' : 'none';
            }
          }}
          data-placeholder={placeholder}
          minHeight={minHeight}
        />

        {attachmentsEditable && selectedImage && (
          <div
            className="tbc-image-resize-panel"
            style={{
              position: 'absolute',
              top: `${resizePanelPos.top}px`,
              left: `${resizePanelPos.left}px`,
              width: '180px',
              background: 'white',
              border: '1px solid rgba(112,115,124,0.22)',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              padding: '8px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              zIndex: 10
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <span style={{ fontSize: 12, color: 'rgba(55,56,60,0.61)' }}>이미지 너비</span>
            <input
              type="range"
              min={20}
              max={100}
              step={5}
              value={imageWidthPercent}
              onChange={(e) => {
                const v = Number(e.target.value);
                setImageWidthPercent(v);
                if (selectedImage) {
                  const style = selectedImage.getAttribute('style') || '';
                  const next = style
                    .replace(/max-width:\s*\d+%/g, '')
                    .trim();
                  selectedImage.setAttribute('style', `max-width: ${v}%; height: auto; margin: 10px 0; ${next}`.trim());
                  onChange(editorRef.current?.innerHTML || '');
                  updateResizePanelPosition();
                }
              }}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={() => {
                if (selectedImage) {
                  selectedImage.setAttribute('style', 'max-width: 100%; height: auto; margin: 10px 0;');
                  setImageWidthPercent(100);
                  onChange(editorRef.current?.innerHTML || '');
                  updateResizePanelPosition();
                }
              }}
              style={{
                border: '1px solid rgba(112,115,124,0.28)',
                borderRadius: 6,
                padding: '2px 6px',
                background: 'white',
                color: 'rgba(55,56,60,0.61)',
                fontSize: 12,
                cursor: 'pointer'
              }}
            >
              100%
            </button>
          </div>
        )}
      </EditorWrapper>

      {/* 첨부파일은 에디터 본문 HTML에 ul.tbc-attachments로 삽입/표시됩니다. */}

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
      />

      <HiddenFileInput
        ref={attachmentInputRef}
        type="file"
        multiple
        onChange={handleAttachmentSelect}
      />
    </EditorContainer>
  );
});

const EditorContainer = styled.div<{ hideBorder?: boolean }>`
  width: 100%;
  border: ${(props) => props.hideBorder ? 'none' : '1px solid rgba(112, 115, 124, 0.22)'};
  border-radius: 12px;
  overflow: hidden;
  background: white;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background: rgba(112, 115, 124, 0.08);
  border-bottom: 1px solid rgba(112, 115, 124, 0.22);
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToolbarSelect = styled.select`
  height: 32px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 8px;
  background: white;
  color: #37383c;
  padding: 0 10px;
  font-size: 13px;
  outline: none;
`;

const ToolbarButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  position: relative;
  width: 32px;
  height: 32px;
  border: none;
  background: ${(props) => props.active ? 'rgba(112, 115, 124, 0.2)' : 'transparent'};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => props.disabled ? 'not-allowed' : 'pointer'};
  color: ${(props) => {
    if (props.disabled) return 'rgba(55, 56, 60, 0.28)';
    return props.active ? '#171719' : 'rgba(55, 56, 60, 0.61)';
  }};
  transition: all 0.2s ease;
  opacity: ${(props) => props.disabled ? 0.5 : 1};

  &:hover {
    background: ${(props) => props.disabled ? 'transparent' : 'rgba(112, 115, 124, 0.15)'};
    color: ${(props) => props.disabled ? 'rgba(55, 56, 60, 0.28)' : '#171719'};
  }

  /* 툴팁 스타일 */
  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba(55, 56, 60, 0.8);
    color: white;
    padding: 6px 8px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 500;
    font-family: 'Pretendard JP', sans-serif;
    white-space: nowrap;
    z-index: 1000;
    pointer-events: none;
    opacity: 1;
    animation: tooltipFadeIn 0.2s ease;
  }

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-2px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(112, 115, 124, 0.22);
`;

const EditorWrapper = styled.div`
  position: relative;
`;

const Editor = styled.div<{ minHeight: string }>`
  min-height: ${(props) => props.minHeight};
  max-height: 500px;
  overflow-y: auto;
  padding: 16px;
  outline: none;
  line-height: 1.6;
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  
  &:empty:before {
    content: attr(data-placeholder);
    color: rgba(55, 56, 60, 0.28);
    pointer-events: none;
  }

  /* 서식 스타일 추가 - global.css의 font: inherit 덮어쓰기 */
  b, strong {
    font-weight: 700 !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  u {
    text-decoration: underline !important;
  }

  strike, s, del {
    text-decoration: line-through !important;
  }

  i, em {
    font-style: italic !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  p {
    margin: 0 0 12px 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  img {
    display: block;
    max-width: 50%;
    height: auto;
    border-radius: 8px;
    margin: 10px 0;
  }

  pre {
    background: rgba(112, 115, 124, 0.08);
    border-radius: 8px;
    padding: 12px;
    margin: 10px 0;
    overflow-x: auto;

    code {
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
    }
  }

  /* 첨부파일 UI 스타일 */
  ul.tbc-attachments {
    list-style: none;
    padding: 0;
    margin: 16px 0 0 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    li.tbc-attachment {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background: rgba(112, 115, 124, 0.05);
      border: 1px solid rgba(112, 115, 124, 0.15);
      border-radius: 8px;
      position: relative;

      .tbc-attachment__link {
        display: flex;
        align-items: center;
        gap: 8px;
        text-decoration: none;
        color: #171719;
        flex: 1;
        min-width: 0;

        .tbc-attachment__thumb-wrap {
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(55, 56, 60, 0.61);
          flex-shrink: 0;

          svg {
            width: 14px;
            height: 14px;
            display: block;
            margin: 0;
            padding: 0;
          }
        }

        .tbc-attachment__name {
          font-size: 14px;
          font-weight: 500;
          color: #171719;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .tbc-attachment__delete {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: 1px solid rgba(55, 56, 60, 0.61);
        border-radius: 4px;
        cursor: pointer;
        color: rgba(55, 56, 60, 0.61);
        transition: all 0.2s ease;
        flex-shrink: 0;
        text-decoration: none;
        outline: none;

        &::after {
          content: '×';
          font-size: 16px;
          font-weight: 500;
          line-height: 1;
        }

        &:hover {
          background: rgba(255, 59, 48, 0.1);
          color: #ff3b30;
          border-color: #ff3b30;
        }
      }

      &:hover {
        background: rgba(112, 115, 124, 0.08);
        border-color: rgba(112, 115, 124, 0.22);
      }
    }
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;


export default RichTextEditor;
